import { Component, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { catchError, finalize, forkJoin } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzTypographyModule } from "ng-zorro-antd/typography";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzTooltipDirective, NzTooltipModule } from "ng-zorro-antd/tooltip";
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzBadgeModule } from 'ng-zorro-antd/badge';

import { MediaTypeEnum, SdkCategoryDto, SdkDto, SdkPlatformEnum, SdkStatusEnum, SdkVendorDto, SdkAssetTypeEnum, DriveFileDto, PermissionEnum } from "../../../dto";
import { HttpSdkService } from "../../../services/sdk/http-sdk.service";
import { SdkActionService } from "./sdk-action.service";
import { SdkEditorModalComponent } from "../sdk-editor/sdk-editor-modal.component";
import { AuthService } from "../../../services/auth/auth.service";

@Component({
    selector: 'app-view-sdk',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzCardModule,
        NzListModule,
        NzBreadCrumbModule,
        NzGridModule,
        NzDividerModule,
        NzDropDownModule,
        SdkEditorModalComponent,
        NzTypographyModule,
        NzTagModule,
        NzTooltipModule,
        NzSpaceModule,
        NzFormModule,
        NzInputModule,
        NzSelectModule,
        NzBadgeModule
    ],
    templateUrl: './view-sdk.component.html',
    styleUrls: ['./view-sdk.component.scss']
})
export class ViewSdkComponent implements OnInit {

    sdks: SdkDto[] = [];
    selectedSdk!: SdkDto;
    SdkStatusEnum = SdkStatusEnum;
    SdkAssetTypeEnum = SdkAssetTypeEnum; // Exposed to template
    loading = false;

    expandedDescriptions = new Set<number>();
    mediaTypeEnum = MediaTypeEnum;
    PermissionEnum = PermissionEnum;

    platform = SdkPlatformEnum;
    platformValues = Object.values(SdkPlatformEnum);

    searchForm!: FormGroup;
    categories: SdkCategoryDto[] = [];
    vendors: SdkVendorDto[] = [];

    @ViewChild(SdkEditorModalComponent) sdkModalComponent!: SdkEditorModalComponent;

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService, 
        private httpService: HttpSdkService, 
        private actionService: SdkActionService,
        public authService: AuthService
    ) { }

    ngOnInit(): void {
        this.initSearchForm();
        this.fetchData();
        this.fetchAll();
    }

    initSearchForm() {
        this.searchForm = this.fb.group({
            name: [null],
            categoryId: [null],
            vendorId: [null],
            platform: [null]
        });
    }

    fetchData() {
        forkJoin({
            categories: this.httpService.getSdkCategories(),
            vendors: this.httpService.getSdkVendors()
        })
        .pipe(
            catchError(error => {
                console.error('Error fetching SDK reference data', error);
                this.toastr.error('Failed to fetch form data: ' + (error.error?.error || error.message));
                throw error;
            })
        )
        .subscribe(data => {
            this.categories = data.categories;
            this.vendors = data.vendors;
        });
    }

    onSearch() {
        this.fetchAll();
    }

    resetForm() {
        this.searchForm.reset();
        this.fetchAll();
    }

    fetchAll() {
        this.loading = true;

        const formVal = this.searchForm.value;
        const search: SdkDto = {};

        if (formVal.name) {
            search.name = formVal.name;
        }
        if (formVal.categoryId) {
            search.category = { id: formVal.categoryId };
        }
        if (formVal.vendorId) {
            search.vendor = { id: formVal.vendorId };
        }
        if (formVal.platform) {
            search.platforms = [formVal.platform]; 
        }

        this.httpService.getAllSdks(search)
            .pipe(
                catchError(error => {
                    console.error('Error fetching SDKs', error);
                    this.toastr.error('Failed to fetch SDKs: ' + (error.error?.error || error.message));
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(data => {
                this.sdks = data.map(sdk => ({ ...sdk, expand: false }));
            });
    }

    // --- Helper methods for the template to extract specific assets ---
    
    getSingleAsset(sdk: SdkDto, type: SdkAssetTypeEnum): DriveFileDto | undefined {
        return sdk.assets?.find(a => a.assetType === type)?.driveFile;
    }

    getAssetsList(sdk: SdkDto, type: SdkAssetTypeEnum): DriveFileDto[] {
        return sdk.assets?.filter(a => a.assetType === type).map(a => a.driveFile!).filter(f => !!f) || [];
    }

    // ------------------------------------------------------------------

    async disable(input: SdkDto) {
        const success = await this.actionService.disableSdk(input);
        if (success) this.fetchAll();
    }

    async enable(input: SdkDto) {
        const success = await this.actionService.enableSdk(input);
        if (success) this.fetchAll();
    }

    async delete(input: SdkDto) {
        const success = await this.actionService.deleteSdk(input);
        if (success) this.fetchAll();
    }

    addSdk() {
        this.sdkModalComponent.open();
    }

    editSdk(input: SdkDto) {
        this.selectedSdk = input;
        this.sdkModalComponent.open(input);
    }

    onSave() {
        this.fetchAll();
    }

    toggleDescription(productId: number): void {
        if (this.expandedDescriptions.has(productId)) {
            this.expandedDescriptions.delete(productId);
        } else {
            this.expandedDescriptions.add(productId);
        }
    }

    viewFile(driveFileLink: string | undefined): void {
        if (driveFileLink) {
            window.open(driveFileLink, '_blank');
        } else {
            this.toastr.error('Could not find the link for this file.');
        }
    }
}